// Catalog.ID Client — PIN Entry Component
// ============================================================================
// Shuffled numeric keypad for PIN entry. Digits are randomized on each render
// to prevent shoulder-surfing and position-based attacks.

import { createElement } from '@catalog-shell';

export interface PinEntryConfig {
    pinLength: 4 | 6 | 8;
    onComplete: (pin: string) => void;
    onCancel?: () => void;
}

export function createPinEntry(config: PinEntryConfig): HTMLElement {
    const { pinLength, onComplete, onCancel } = config;
    let entered = '';

    const container = createElement('div', { className: 'cid-pin-entry' });

    // Dots display
    const dotsRow = createElement('div', { className: 'cid-pin-dots' });
    const dots: HTMLElement[] = [];
    for (let i = 0; i < pinLength; i++) {
        const dot = createElement('div', { className: 'cid-pin-dot' });
        dots.push(dot);
        dotsRow.appendChild(dot);
    }
    container.appendChild(dotsRow);

    // Shuffled digit buttons
    const keypad = createElement('div', { className: 'cid-pin-keypad' });
    const digits = shuffleArray([0, 1, 2, 3, 4, 5, 6, 7, 8, 9]);

    for (const digit of digits) {
        const btn = createElement('button', {
            className: 'cid-pin-key cs-btn',
            textContent: String(digit),
        });
        btn.addEventListener('click', () => pressDigit(String(digit)));
        keypad.appendChild(btn);
    }

    // Bottom row: cancel (optional) + backspace
    if (onCancel) {
        const cancelBtn = createElement('button', {
            className: 'cid-pin-key cid-pin-key--action cs-btn',
            textContent: 'Cancel',
        });
        cancelBtn.addEventListener('click', onCancel);
        keypad.appendChild(cancelBtn);
    } else {
        keypad.appendChild(createElement('div', { className: 'cid-pin-key cid-pin-key--spacer' }));
    }

    const backBtn = createElement('button', {
        className: 'cid-pin-key cid-pin-key--action cs-btn',
        textContent: '\u232B', // backspace symbol ⌫
    });
    backBtn.addEventListener('click', pressBackspace);
    keypad.appendChild(backBtn);

    container.appendChild(keypad);

    function pressDigit(d: string): void {
        if (entered.length >= pinLength) return;
        entered += d;
        updateDots();
        if (entered.length === pinLength) {
            onComplete(entered);
        }
    }

    function pressBackspace(): void {
        if (entered.length === 0) return;
        entered = entered.slice(0, -1);
        updateDots();
    }

    function updateDots(): void {
        for (let i = 0; i < pinLength; i++) {
            dots[i].classList.toggle('cid-pin-dot--filled', i < entered.length);
        }
    }

    // Attach reset function to the element for external access
    (container as PinEntryElement).__resetPin = () => {
        entered = '';
        updateDots();
    };

    return container;
}

export interface PinEntryElement extends HTMLElement {
    __resetPin?: () => void;
}

/** Reset the PIN entry (clear entered digits and internal state). */
export function resetPinEntry(container: HTMLElement): void {
    const el = container as PinEntryElement;
    if (el.__resetPin) {
        el.__resetPin();
    }
}

function shuffleArray<T>(arr: T[]): T[] {
    const result = [...arr];
    for (let i = result.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
}
